/**
 * Tenant Edit — cadastro comercial completo (RDC ANVISA).
 *
 * Layout em tabs:
 *   - Empresa            (CompanyTab)
 *   - Responsáveis       (ResponsaveisTab)
 *   - Licenças           (LicencasTab)
 *   - Identidade Visual  (IdentityTab)
 *   - Auditoria          (AuditTab — só super_admin)
 *
 * Header tem indicador de completude % e badge regulatory_ready.
 */

import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  ClipboardList,
  FileBadge,
  History,
  Image as ImageIcon,
  Users as UsersIcon,
} from "lucide-react";
import clsx from "clsx";

import {
  useTenantCompany,
  useTenantCompleteness,
} from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";

import { Loading } from "@/components/Loading";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/Badge";
import { Card, CardBody } from "@/components/Card";

import { CompanyTab } from "./tenants/CompanyTab";
import { ResponsaveisTab } from "./tenants/ResponsaveisTab";
import { LicencasTab } from "./tenants/LicencasTab";
import { IdentityTab } from "./tenants/IdentityTab";
import { AuditTab } from "./tenants/AuditTab";

// ---------- Tabs ----------

type TabKey = "company" | "responsaveis" | "licencas" | "identity" | "audit";

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  superAdminOnly?: boolean;
}

const TABS: TabDef[] = [
  { key: "company",      label: "Empresa",           icon: <Building2 className="h-4 w-4" /> },
  { key: "responsaveis", label: "Responsáveis",      icon: <UsersIcon className="h-4 w-4" /> },
  { key: "licencas",     label: "Licenças",          icon: <FileBadge className="h-4 w-4" /> },
  { key: "identity",     label: "Identidade Visual", icon: <ImageIcon className="h-4 w-4" /> },
  { key: "audit",        label: "Auditoria",         icon: <History className="h-4 w-4" />, superAdminOnly: true },
];

// ---------- Page ----------

export function TenantEditPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const company = useTenantCompany(id);
  const completeness = useTenantCompleteness(id);
  const [activeTab, setActiveTab] = useState<TabKey>("company");

  if (!id) return <Loading label="Carregando tenant..." />;

  // Tenant admin só pode editar o próprio — redireciona se tentar outro via URL
  if (!auth.isSuperAdmin && auth.user?.tenant_id && id !== auth.user.tenant_id) {
    return <Navigate to={`/admin/tenants/${auth.user.tenant_id}/edit`} replace />;
  }

  const visibleTabs = TABS.filter((t) => !t.superAdminOnly || auth.isSuperAdmin);

  const titleSuffix = company.data?.razao_social || company.data?.nome_fantasia || id.slice(0, 8);

  return (
    <div className="mx-auto max-w-6xl">
      {/* "Voltar pra lista" só faz sentido pra super_admin (que tem acesso à lista) */}
      {auth.isSuperAdmin && (
        <Link
          to="/admin/tenants"
          className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar pra lista
        </Link>
      )}

      <PageHeader
        voltar
        title={`Cadastro Comercial`}
        description={`Tenant: ${titleSuffix}`}
        actions={<CompletenessBadge tenantId={id} />}
      />

      {/* Banner — cadastro incompleto */}
      {completeness.data && !completeness.data.is_regulatory_ready && (
        <Card className="mb-4 border-amber-200 dark:border-amber-900/40 !bg-amber-50 dark:!bg-amber-950/30">
          <CardBody className="py-3 text-sm text-amber-900 dark:text-amber-200">
            <div className="flex items-start gap-2">
              <ClipboardList className="mt-0.5 h-4 w-4 flex-none text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium">Cadastro incompleto pra fins regulatórios.</p>
                <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                  Faltando: {completeness.data.missing_fields.map(humanizeField).join(", ")}.
                  Relatórios serão gerados, mas marcados como "informativos" até completar.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tabs nav */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-surface-200 dark:border-slate-800">
        {visibleTabs.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={clsx(
                "inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition",
                isActive
                  ? "border-brand-500 text-brand-700"
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800",
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "company"      && <CompanyTab tenantId={id} />}
      {activeTab === "responsaveis" && <ResponsaveisTab tenantId={id} />}
      {activeTab === "licencas"     && <LicencasTab tenantId={id} />}
      {activeTab === "identity"     && <IdentityTab tenantId={id} />}
      {activeTab === "audit"        && auth.isSuperAdmin && <AuditTab tenantId={id} />}
    </div>
  );
}

// ---------- Completeness badge no header ----------

function CompletenessBadge({ tenantId }: { tenantId: string }) {
  const c = useTenantCompleteness(tenantId);
  if (!c.data) return null;

  const pct = c.data.completeness_pct;
  const tone =
    c.data.is_regulatory_ready ? "success" :
    pct >= 60                   ? "warning" :
                                  "danger";

  return (
    <div className="flex items-center gap-2">
      <Badge tone={tone}>
        {c.data.is_regulatory_ready
          ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Dados Regulatórios Completos</span>
          : `${pct}% completo`}
      </Badge>
    </div>
  );
}

// ---------- Helpers ----------

const FIELD_LABELS: Record<string, string> = {
  razao_social:                       "Razão Social",
  cnpj:                               "CNPJ",
  tipo_estabelecimento:               "Tipo de Estabelecimento",
  endereco_cep:                       "CEP",
  endereco_logradouro:                "Logradouro",
  endereco_numero:                    "Número",
  endereco_cidade:                    "Cidade",
  endereco_uf:                        "UF",
  telefone:                           "Telefone",
  email:                              "E-mail",
  responsavel_tecnico_com_conselho:   "Responsável Técnico (com conselho)",
};

function humanizeField(key: string): string {
  return FIELD_LABELS[key] ?? key;
}
